/* eslint-disable @typescript-eslint/prefer-nullish-coalescing --
   Ported verbatim from cpanel `getRouteTimerProps`. The `|| []` and
   `|| undefined` fallbacks are intentional falsy-collapse; `??` would change
   behavior on empty arrays / empty strings. Preserve byte-for-byte. */
import { OrderStage } from '../model/stages';
import { isTimerStopped } from './stage';

/**
 * Route timer derivation, ported VERBATIM from cpanel `getRouteTimerProps`
 * (`model/helpers.tsx`). Drives the elapsed-time column countdown: a live timer
 * counting toward an ETA, or a frozen badge (`onlyShow`) once a point is done.
 * The data shape is loose (matches the broad backend row); only the touched
 * fields are typed.
 */

interface RoutePoint {
  arrived_at?: string | null | undefined;
  handed_over_at?: string | null | undefined;
  initial_eta?: string | null | undefined;
}

interface TimerData {
  stage?: number;
  search_started_at?: string | null;
  search_stopped_at?: string | null;
  origin_full?: RoutePoint[] | null;
  destination_full?: RoutePoint[] | null;
}

export interface RouteTimerProps {
  endTime: string;
  doneAt: string | undefined;
  onlyShow: boolean;
}

export function getRouteTimerProps(
  data: TimerData | null | undefined,
): RouteTimerProps | null {
  if (!data) return null;
  const stage = data.stage ?? 0;
  const onlyShow = isTimerStopped(stage);

  // 1. Search phase (stages 1-6) and PERFORMER_NOT_FOUND: count from
  //    search_started_at, frozen at search_stopped_at.
  const isSearchPhase = stage >= OrderStage.NEW && stage <= OrderStage.PERFORMER_DRAFT;
  if (isSearchPhase || stage === OrderStage.PERFORMER_NOT_FOUND) {
    if (!data.search_started_at) return null;
    return {
      endTime: data.search_started_at,
      doneAt: data.search_stopped_at || undefined,
      onlyShow: !!data.search_stopped_at || onlyShow,
    };
  }

  const routeData = [...(data.origin_full || []), ...(data.destination_full || [])];
  if (routeData.length === 0) return null;

  // 2. Find the currently active route point.
  const activePoint =
    routeData.find((r) => !r.arrived_at || !r.handed_over_at) ??
    routeData[routeData.length - 1];
  if (!activePoint) return null;
  const activeIndex = routeData.indexOf(activePoint);

  const prevStops = routeData.slice(0, activeIndex);
  const arePrevCompleted = prevStops.every((r) => r.arrived_at && r.handed_over_at);
  const shouldShowArrival = activeIndex === 0 || arePrevCompleted;

  // 3. Courier hasn't arrived yet at this point.
  if (!activePoint.arrived_at) {
    if (!shouldShowArrival || !activePoint.initial_eta) return null;
    if (onlyShow) return null;
    return { endTime: activePoint.initial_eta, doneAt: undefined, onlyShow: false };
  }

  // 4. Courier arrived but hasn't handed over → waiting timer.
  if (activePoint.arrived_at && !activePoint.handed_over_at) {
    if (onlyShow) return null;
    return { endTime: activePoint.arrived_at, doneAt: undefined, onlyShow: false };
  }

  // 5. Both arrived_at and handed_over_at exist — frozen arrival timer.
  return {
    endTime: activePoint.initial_eta ?? '',
    doneAt: activePoint.arrived_at,
    onlyShow: true,
  };
}
